import * as React from "react";
import { format } from "date-fns";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";

import { LocationPhotosSlider } from "~/features/pickup-location-modal/components/location-photos-slider";

interface AdditionalPhotosTabProps {
  job: ConnectProto.Job;
  isLoading?: boolean;
}

export const AdditionalPhotosTab: React.FC<AdditionalPhotosTabProps> = ({
  job,
  isLoading = false,
}) => {
  const t = useT();
  const [isSliderOpen, setIsSliderOpen] = React.useState(false);
  const [selectedPhotoIndex, setSliderStartIndex] = React.useState(0);

  const photos = React.useMemo(() => {
    const fileReports = job.fileReports ?? [];
    return fileReports.filter(
      (item) => item.type === ConnectProto.FileUploadType.PICKUP_LOCATION_VIEW,
    );
  }, [job]);

  const formatDate = (dateInput: Date | undefined): { time: string; date: string } => {
    if (!dateInput) {
      return { time: "", date: "" };
    }
    try {
      return {
        time: format(dateInput, "HH:mm"),
        date: format(dateInput, "dd.MM.yy"),
      };
    } catch {
      return { time: "", date: "" };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center my-4">
        <p className="text-text-secondary">{t(Namespace.Common, "loading")}...</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center my-8">
        <p className="text-text-secondary">{t(Namespace.PhotoReport, "noAdditionalPhotos")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
      {photos.length > 0 &&
        photos.map((photo, index) => {
          const dateInfo = formatDate(fromTimestamp(photo.createdAt) ?? undefined);
          return (
            <div
              key={photo.id}
              style={{ backgroundImage: `url("${photo.url}")` }}
              className="aspect-[4/3] rounded-2xl bg-cover bg-center cursor-pointer relative"
              onClick={() => {
                setSliderStartIndex(index);
                setIsSliderOpen(true);
              }}
            >
              <div className="absolute top-2 left-2 bg-surface-invert flex items-center justify-center gap-4 h-8 px-2 py-2 rounded-lg">
                <p className="text-sm text-[#F2F4F7] leading-[14px] tracking-[-0.14px]">
                  {dateInfo.time}
                </p>
                <p className="text-sm text-[#F2F4F7] leading-[14px] tracking-[-0.14px]">
                  {dateInfo.date}
                </p>
              </div>
            </div>
          );
        })}

      <LocationPhotosSlider
        photos={photos}
        open={isSliderOpen}
        onOpenChange={setIsSliderOpen}
        initialSlide={selectedPhotoIndex}
        title={t(Namespace.PhotoReport, "additionalPhotos")}
      />
    </div>
  );
};
